import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { ValidateEmailData } from '@viome/viome-base/src/usecase/Register/Register.model';
import useRegistration from './useRegistration';
import { useNavigate } from 'react-router-dom';
import { useAccountCreationMutation } from '@viome/viome-base/src/usecase/Register/RegisterQuery';
import { useToast } from '../use-toast';
import * as P from '@/constants/paths';
import useRegisterStore, { RegistrationTrack } from '../useRegistrationStore';
import { deSanitizedKitId } from './useRegistrationKitId';

const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one numeric character')
  .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character');

const defaultSchema = z
  .object({
    email: z.string().email('Please enter a valid email'),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Your passwords do not match. Please try again.',
    path: ['confirmPassword'],
  });

const useAccountCreation = () => {
  const { kitId } = useRegistration();
  const navigate = useNavigate();
  const {
    email,
    password,
    setAccountCreation,
    setRestrictedRegistrationSteps,
  } = useRegisterStore();
  const { mutate: validateEmail, isPending: accountCreationPending } =
    useAccountCreationMutation();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof defaultSchema>>({
    mode: 'onSubmit',
    resolver: zodResolver(defaultSchema),
    defaultValues: {
      email: email,
      password: password,
      confirmPassword: '',
    },
  });
  
  const onSubmit = (input: { email: string; password: string }) => {
    const formData: ValidateEmailData = {
      email: input.email,
      kitId: deSanitizedKitId(kitId ?? ''),
    };

    validateEmail(formData, {
      onSuccess: (response) => {
        if (response.valid) {
          Promise.all([
            setAccountCreation(input.email, input.password),
            setRestrictedRegistrationSteps([
              RegistrationTrack.ContactInformation,
              RegistrationTrack.AddressConfirmation,
              RegistrationTrack.RegistrationConfirmation,
              RegistrationTrack.TermsOfService,
              RegistrationTrack.ConsentForm,
            ]),
            navigate(P.REGISTRATION_PERSONAL_INFORMATION),
          ]);
        } else {
          console.error('Email is not valid.');
          toast({
            description: 'Email is not valid.',
          });
        }
      },
      onError: (error) => {
        console.error('Validation failed:', error);
        toast({
          variant: 'destructive',
          description: error.message,
        });
      },
    });
  };

  return {
    form,
    onSubmit,
    accountCreationPending,
  };
};

export default useAccountCreation;